import React, { useEffect, useState, useMemo } from "react";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";

const LocationCompareModal = ({
  isOpen = true,
  onClose,
  locationId,
  initialVersion = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(initialVersion);
  const [masterData, setMasterData] = useState(null);
  const [googlePlacesData, setGooglePlacesData] = useState(null);
  const [googleBusinessData, setGoogleBusinessData] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'diff' | 'match'
  const [activeSection, setActiveSection] = useState("all");

  // Fetch comparison data from backend
  const fetchComparison = async (ver = null) => {
    if (!locationId) return;
    setLoading(true);
    try {
      const params = {};
      if (ver) params.version = ver;

      const res = await api.get(`/main-locations/${locationId}/compare`, { params });
      if (res.data?.success) {
        setAvailableVersions(res.data.availableVersions || []);
        setSelectedVersion(res.data.selectedVersion);
        setMasterData(res.data.masterData || null);
        setGooglePlacesData(res.data.googlePlacesData || null);
        setGoogleBusinessData(res.data.googleBusinessData || null);
      } else {
        showToast("error", res.data?.message || "Failed to load comparison data.");
      }
    } catch (err) {
      showToast("error", err.response?.data?.message || "Error fetching comparison details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && locationId) {
      fetchComparison(initialVersion);
    }
  }, [isOpen, locationId]);

  const handleVersionChange = (e) => {
    const newVer = Number(e.target.value);
    setSelectedVersion(newVer);
    fetchComparison(newVer);
  };

  // Helper: Format Date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(dateStr);
    }
  };

  // Helper: Value Normalizer for Comparison
  const normalize = (val) => {
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "boolean") return val ? "true" : "false";
    if (typeof val === "number") return String(Number(val.toFixed(5)));
    if (typeof val === "object") {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val).trim().toLowerCase().replace(/[\s\-_()+]/g, "");
  };

  // Helper: Compare 3 values
  const computeDiffStatus = (v1, v2, v3) => {
    const n1 = normalize(v1);
    const n2 = normalize(v2);
    const n3 = normalize(v3);

    // Count non-empty values
    const nonEmpties = [n1, n2, n3].filter((v) => v !== null);

    if (nonEmpties.length === 0) return "empty";
    if (nonEmpties.length === 1) return "single";

    // Check if all non-empty values are identical
    const first = nonEmpties[0];
    const allMatch = nonEmpties.every((v) => v === first);

    if (allMatch) {
      return nonEmpties.length === 3 ? "exact_match" : "partial_match";
    }
    return "different";
  };

  // Helper to extract operating hours string
  const formatHoursDisplay = (hours) => {
    if (!hours) return null;
    if (typeof hours === "string") {
      try {
        const parsed = JSON.parse(hours);
        if (Array.isArray(parsed)) return parsed.join(", ");
        if (typeof parsed === "object") {
          return Object.entries(parsed)
            .map(([day, time]) => `${day}: ${time}`)
            .join(" | ");
        }
      } catch {
        return hours;
      }
    }
    if (typeof hours === "object") {
      return Object.entries(hours)
        .map(([day, time]) => `${day}: ${time}`)
        .join(" | ");
    }
    return String(hours);
  };

  // Construct Attribute Rows for comparison
  const attributeRows = useMemo(() => {
    if (!masterData && !googlePlacesData && !googleBusinessData) return [];

    const m = masterData || {};
    const p = googlePlacesData || {};
    const g = googleBusinessData || {};

    const rows = [
      // SECTION: Identity & Overview
      {
        section: "Identity & Overview",
        icon: "solar:user-id-bold-duotone",
        key: "location_name",
        label: "Location Name / Title",
        master: m.location_name,
        gbp: g.title,
        places: p.title,
      },
      {
        section: "Identity & Overview",
        icon: "solar:user-id-bold-duotone",
        key: "store_code",
        label: "Store Code",
        master: m.store_code,
        gbp: g.store_code,
        places: p.store_code,
      },
      {
        section: "Identity & Overview",
        icon: "solar:user-id-bold-duotone",
        key: "primary_category",
        label: "Primary Category",
        master: m.primary_category_id || m.additional_category,
        gbp: g.primary_category_name || g.primary_category_id,
        places: p.primary_category_name || p.primary_category_id,
      },
      {
        section: "Identity & Overview",
        icon: "solar:user-id-bold-duotone",
        key: "description",
        label: "Description / Editorial",
        master: m.description,
        gbp: g.description,
        places: p.description,
      },
      {
        section: "Identity & Overview",
        icon: "solar:user-id-bold-duotone",
        key: "status",
        label: "Business Status",
        master: m.sync_status || "ACTIVE",
        gbp: g.open_status
          ? `${g.open_status} (${
              g.is_confirmed === true ||
              ["VERIFIED", "CONFIRMED", "TRUE", "1"].includes(
                String(g.is_verified || "").toUpperCase()
              )
                ? "Confirmed"
                : String(g.is_verified || "").toUpperCase() === "PENDING"
                  ? "Pending"
                  : "Not Confirmed"
            })`
          : null,
        places: p.business_status || p.open_status,
      },

      // SECTION: Address & Geolocation
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "street_address",
        label: "Street Address",
        master: m.street_address,
        gbp: g.address_line1 || g.street_address,
        places: p.street_address || p.address_line1,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "house_number",
        label: "House Number",
        master: m.house_number,
        gbp: g.house_number || g.address_line2,
        places: p.house_number,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "postal_code",
        label: "Postal Code",
        master: m.postal_code,
        gbp: g.postal_code,
        places: p.postal_code,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "city",
        label: "City",
        master: m.city,
        gbp: g.city,
        places: p.city,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "country",
        label: "Country / Code",
        master: m.country,
        gbp: g.country_code,
        places: p.country_code,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "latitude",
        label: "Latitude",
        master: m.lat,
        gbp: g.latitude,
        places: p.latitude,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "longitude",
        label: "Longitude",
        master: m.lng,
        gbp: g.longitude,
        places: p.longitude,
      },
      {
        section: "Address & Geolocation",
        icon: "solar:map-point-wave-bold-duotone",
        key: "google_place_id",
        label: "Google Place ID",
        master: m.google_place_id,
        gbp: g.google_location_id,
        places: p.google_place_id,
      },

      // SECTION: Contact & Online
      {
        section: "Contact & Online",
        icon: "solar:phone-calling-rounded-bold-duotone",
        key: "phone_number",
        label: "Primary Phone Number",
        master: m.phone_number ? `${m.phone_country_code || ""} ${m.phone_number}`.trim() : null,
        gbp: g.primary_phone,
        places: p.primary_phone || p.international_phone,
      },
      {
        section: "Contact & Online",
        icon: "solar:phone-calling-rounded-bold-duotone",
        key: "website_url",
        label: "Website URI",
        master: m.website_url,
        gbp: g.website_uri,
        places: p.website_uri,
      },
      {
        section: "Contact & Online",
        icon: "solar:phone-calling-rounded-bold-duotone",
        key: "google_link",
        label: "Google Maps Link",
        master: m.google_link,
        gbp: g.maps_uri,
        places: p.maps_uri,
      },

      // SECTION: Operating Hours
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_monday",
        label: "Monday Hours",
        master: m.hours_monday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_tuesday",
        label: "Tuesday Hours",
        master: m.hours_tuesday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_wednesday",
        label: "Wednesday Hours",
        master: m.hours_wednesday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_thursday",
        label: "Thursday Hours",
        master: m.hours_thursday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_friday",
        label: "Friday Hours",
        master: m.hours_friday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_saturday",
        label: "Saturday Hours",
        master: m.hours_saturday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },
      {
        section: "Operating Hours",
        icon: "solar:clock-circle-bold-duotone",
        key: "hours_sunday",
        label: "Sunday Hours",
        master: m.hours_sunday,
        gbp: formatHoursDisplay(g.regular_hours),
        places: formatHoursDisplay(p.regular_hours),
      },

      // SECTION: Parking & Accessibility
      {
        section: "Parking & Accessibility",
        icon: "solar:wheelchair-bold-duotone",
        key: "wheelchair_accessible_entrance",
        label: "Wheelchair Accessible Entrance",
        master: m.wheelchair_accessible_entrance,
        gbp: null,
        places: null,
      },
      {
        section: "Parking & Accessibility",
        icon: "solar:wheelchair-bold-duotone",
        key: "on_site_parking",
        label: "On-Site Parking",
        master: m.on_site_parking,
        gbp: null,
        places: null,
      },
      {
        section: "Parking & Accessibility",
        icon: "solar:wheelchair-bold-duotone",
        key: "free_parking_lot",
        label: "Free Parking Lot",
        master: m.free_parking_lot,
        gbp: null,
        places: null,
      },
      {
        section: "Parking & Accessibility",
        icon: "solar:wheelchair-bold-duotone",
        key: "paid_parking_lot",
        label: "Paid Parking Lot",
        master: m.paid_parking_lot,
        gbp: null,
        places: null,
      },

      // SECTION: Metadata & System
      {
        section: "Metadata & System",
        icon: "solar:info-circle-bold-duotone",
        key: "updated_at",
        label: "Last Updated Date",
        master: formatDate(m.updated_at),
        gbp: formatDate(g.updated_at),
        places: formatDate(p.updated_at),
      },
    ];

    return rows.map((r) => ({
      ...r,
      diffStatus: computeDiffStatus(r.master, r.gbp, r.places),
    }));
  }, [masterData, googlePlacesData, googleBusinessData]);

  // Compute Summary Statistics
  const stats = useMemo(() => {
    let exactMatches = 0;
    let differences = 0;
    let singleOrMissing = 0;

    attributeRows.forEach((row) => {
      if (row.diffStatus === "exact_match" || row.diffStatus === "partial_match") {
        exactMatches++;
      } else if (row.diffStatus === "different") {
        differences++;
      } else {
        singleOrMissing++;
      }
    });

    return {
      total: attributeRows.length,
      exactMatches,
      differences,
      singleOrMissing,
    };
  }, [attributeRows]);

  // Filtered Rows
  const filteredRows = useMemo(() => {
    return attributeRows.filter((r) => {
      // Section filter
      if (activeSection !== "all" && r.section !== activeSection) return false;

      // Diff filter
      if (filterMode === "diff") return r.diffStatus === "different";
      if (filterMode === "match") return r.diffStatus === "exact_match" || r.diffStatus === "partial_match";
      return true;
    });
  }, [attributeRows, activeSection, filterMode]);

  // Sections List
  const sections = useMemo(() => {
    const set = new Set(attributeRows.map((r) => r.section));
    return ["all", ...Array.from(set)];
  }, [attributeRows]);

  if (!isOpen) return null;

  const currentVersionMeta = availableVersions.find((v) => v.version_number === selectedVersion);

  const renderValue = (val, isDiff = false) => {
    if (val === null || val === undefined || val === "") {
      return <span className="text-secondary-light fs-7 fst-italic">— Not Provided —</span>;
    }
    if (typeof val === "boolean") {
      return (
        <span
          className={`badge px-8 py-3 radius-4 text-xs fw-semibold ${
            val ? "bg-success-focus text-success-main" : "bg-neutral-200 text-neutral-600"
          }`}
        >
          {val ? "YES" : "NO"}
        </span>
      );
    }

    const strVal = String(val);
    if (strVal.startsWith("http://") || strVal.startsWith("https://")) {
      return (
        <a
          href={strVal}
          target="_blank"
          rel="noreferrer"
          className="text-primary-600 text-decoration-underline d-inline-flex align-items-center gap-1 text-xs"
          style={{ wordBreak: "break-all" }}
        >
          <Icon icon="solar:link-linear" />
          {strVal.length > 35 ? strVal.substring(0, 32) + "..." : strVal}
        </a>
      );
    }

    return (
      <span className={`text-sm ${isDiff ? "fw-bold text-dark" : "text-secondary-dark"}`} style={{ wordBreak: "break-word" }}>
        {strVal}
      </span>
    );
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.65)", zIndex: 1055, backdropFilter: "blur(4px)" }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered modal-xl" style={{ maxWidth: "1380px" }}>
        <div className="modal-content radius-16 border-0 shadow-lg overflow-hidden bg-base">
          
          {/* MODAL HEADER */}
          <div className="modal-header bg-neutral-50 border-bottom px-24 py-16 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="w-48-px h-48-px bg-primary-100 text-primary-600 radius-12 d-flex align-items-center justify-content-center">
                <Icon icon="solar:scale-bold" className="text-2xl" />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="modal-title fw-bold mb-0 text-dark">
                    3-Way Location Data Comparison
                  </h5>
                  <span className="badge bg-primary-50 text-primary-600 border border-primary-200 px-8 py-4 radius-6 text-xs">
                    ID: #{locationId}
                  </span>
                </div>
                <p className="text-secondary-light text-xs mb-0 mt-1 d-flex align-items-center gap-2 flex-wrap">
                  <span>Target: <strong>{masterData?.location_name || "Location Profile"}</strong></span>
                  {masterData?.store_code && (
                    <span className="badge bg-neutral-200 text-neutral-700 px-6 py-2 radius-4">
                      Store Code: {masterData.store_code}
                    </span>
                  )}
                  {masterData?.google_place_id && (
                    <span className="badge bg-neutral-200 text-neutral-700 px-6 py-2 radius-4">
                      Place ID: {masterData.google_place_id}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* TOP RIGHT: DATE PICKER / CHANGE DATES DROPDOWN */}
            <div className="d-flex align-items-center gap-3 ms-auto">
              <div className="d-flex flex-column align-items-end">
                <div className="d-flex align-items-center gap-2 bg-white px-12 py-6 radius-8 border shadow-sm">
                  <Icon icon="solar:calendar-date-bold-duotone" className="text-primary-600 text-lg" />
                  <span className="text-xs fw-bold text-secondary-light text-uppercase">Change Date:</span>
                  <select
                    className="form-select form-select-sm border-0 fw-semibold text-dark shadow-none bg-transparent pointer"
                    style={{ minWidth: "240px", cursor: "pointer" }}
                    value={selectedVersion || ""}
                    onChange={handleVersionChange}
                    disabled={loading || availableVersions.length === 0}
                  >
                    {availableVersions.map((ver, idx) => (
                      <option key={ver.version_number} value={ver.version_number}>
                        v{ver.version_number} • {formatDate(ver.updated_at || ver.created_at)} {idx === 0 ? "(Latest)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {currentVersionMeta?.user_name && (
                  <span className="text-2xs text-secondary-light mt-1">
                    Modified by: <strong>{currentVersionMeta.user_name}</strong>
                  </span>
                )}
              </div>

              <button
                type="button"
                className="btn-close radius-circle p-8 hover-bg-neutral-200 transition-all shadow-none"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
          </div>

          {/* STATS & FILTER TOOLBAR */}
          <div className="px-24 py-12 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
            
            {/* Diff Counters */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="badge bg-neutral-100 text-neutral-700 border px-12 py-6 radius-8 d-flex align-items-center gap-2 text-xs">
                <Icon icon="solar:checklist-minimalistic-linear" className="text-sm" />
                <span>Total Fields: <strong>{stats.total}</strong></span>
              </span>
              <span className="badge bg-success-50 text-success-600 border border-success-200 px-12 py-6 radius-8 d-flex align-items-center gap-2 text-xs">
                <Icon icon="solar:check-circle-bold" className="text-sm" />
                <span>Matches: <strong>{stats.exactMatches}</strong></span>
              </span>
              <span className="badge bg-warning-50 text-warning-700 border border-warning-300 px-12 py-6 radius-8 d-flex align-items-center gap-2 text-xs">
                <Icon icon="solar:danger-triangle-bold" className="text-sm" />
                <span>Differences: <strong>{stats.differences}</strong></span>
              </span>
              {stats.singleOrMissing > 0 && (
                <span className="badge bg-neutral-50 text-secondary-light border px-10 py-6 radius-8 d-flex align-items-center gap-1 text-xs">
                  <span>Partial/Single: {stats.singleOrMissing}</span>
                </span>
              )}
            </div>

            {/* Filter Toggle Buttons */}
            <div className="d-flex align-items-center gap-2">
              <span className="text-xs text-secondary-light fw-medium me-1">Filter:</span>
              <div className="btn-group btn-group-sm radius-8 overflow-hidden border">
                <button
                  type="button"
                  className={`btn ${filterMode === "all" ? "btn-primary" : "btn-light text-secondary-dark"}`}
                  onClick={() => setFilterMode("all")}
                >
                  All ({stats.total})
                </button>
                <button
                  type="button"
                  className={`btn ${filterMode === "diff" ? "btn-warning text-dark fw-bold" : "btn-light text-secondary-dark"}`}
                  onClick={() => setFilterMode("diff")}
                >
                  Differences ({stats.differences})
                </button>
                <button
                  type="button"
                  className={`btn ${filterMode === "match" ? "btn-success" : "btn-light text-secondary-dark"}`}
                  onClick={() => setFilterMode("match")}
                >
                  Matches ({stats.exactMatches})
                </button>
              </div>
            </div>
          </div>

          {/* SECTION PILLS */}
          <div className="px-24 py-8 bg-neutral-50 border-bottom d-flex gap-2 overflow-x-auto">
            {sections.map((sec) => (
              <button
                key={sec}
                type="button"
                onClick={() => setActiveSection(sec)}
                className={`btn btn-sm py-4 px-12 radius-6 text-xs text-capitalize text-nowrap border-0 ${
                  activeSection === sec ? "bg-primary-600 text-white fw-bold shadow-sm" : "bg-white text-secondary-light hover-bg-neutral-200"
                }`}
              >
                {sec === "all" ? "All Sections" : sec}
              </button>
            ))}
          </div>

          {/* MAIN COMPARISON MATRIX TABLE */}
          <div className="modal-body p-0 overflow-y-auto" style={{ maxHeight: "calc(82vh - 220px)" }}>
            {loading ? (
              <div className="text-center py-60">
                <div className="spinner-border text-primary-600 mb-12" role="status"></div>
                <p className="text-secondary-light text-sm mb-0">Loading snapshot comparison...</p>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center py-60">
                <Icon icon="solar:info-circle-linear" className="text-4xl text-secondary-light mb-8" />
                <p className="text-secondary-light text-sm">No attributes found for current filter.</p>
              </div>
            ) : (
              <div className="table-responsive mb-0">
                <table className="table align-middle table-hover mb-0" style={{ minWidth: "1100px" }}>
                  <thead className="bg-neutral-100 position-sticky top-0 shadow-xs" style={{ zIndex: 10 }}>
                    <tr className="border-bottom">
                      <th className="py-14 px-20 text-xs text-uppercase text-secondary-light fw-bold" style={{ width: "22%" }}>
                        Attribute / Field
                      </th>
                      
                      {/* Master Data Column */}
                      <th className="py-14 px-20 text-xs text-uppercase fw-bold text-primary-700 bg-primary-50 border-start border-end" style={{ width: "26%" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="d-flex align-items-center gap-2">
                            <Icon icon="solar:database-bold" className="text-base text-primary-600" />
                            Master Data
                          </span>
                          <span className="badge bg-primary-600 text-white px-8 py-3 radius-4 text-2xs">
                            v{selectedVersion || 1}
                          </span>
                        </div>
                      </th>

                      {/* Google Business Profile Column */}
                      <th className="py-14 px-20 text-xs text-uppercase fw-bold text-success-800 bg-success-50 border-end" style={{ width: "26%" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="d-flex align-items-center gap-2">
                            <Icon icon="logos:google-icon" className="text-sm" />
                            Google Business Profile
                          </span>
                          {googleBusinessData ? (
                            <span className="badge bg-success-600 text-white px-8 py-3 radius-4 text-2xs">
                              Connected
                            </span>
                          ) : (
                            <span className="badge bg-neutral-300 text-neutral-600 px-8 py-3 radius-4 text-2xs">
                              Not Linked
                            </span>
                          )}
                        </div>
                      </th>

                      {/* Google Places Data Column */}
                      <th className="py-14 px-20 text-xs text-uppercase fw-bold text-info-800 bg-info-50 border-end" style={{ width: "26%" }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="d-flex align-items-center gap-2">
                            <Icon icon="solar:map-point-bold-duotone" className="text-base text-info-600" />
                            Google Places Data
                          </span>
                          {googlePlacesData ? (
                            <span className="badge bg-info-600 text-white px-8 py-3 radius-4 text-2xs">
                              Place Found
                            </span>
                          ) : (
                            <span className="badge bg-neutral-300 text-neutral-600 px-8 py-3 radius-4 text-2xs">
                              Not Found
                            </span>
                          )}
                        </div>
                      </th>

                      {/* Match Status */}
                      <th className="py-14 px-16 text-center text-xs text-uppercase text-secondary-light fw-bold" style={{ width: "10%" }}>
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((row, index) => {
                      const isDiff = row.diffStatus === "different";
                      const isMatch = row.diffStatus === "exact_match" || row.diffStatus === "partial_match";

                      return (
                        <tr
                          key={`${row.section}-${row.key}-${index}`}
                          className={isDiff ? "bg-warning-50" : ""}
                        >
                          {/* Field Label */}
                          <td className="py-12 px-20">
                            <div className="d-flex align-items-center gap-2">
                              <Icon icon={row.icon} className="text-primary-600 text-base flex-shrink-0" />
                              <div>
                                <span className="fw-semibold text-dark text-sm d-block">{row.label}</span>
                                <span className="text-2xs text-secondary-light">{row.section}</span>
                              </div>
                            </div>
                          </td>

                          {/* Master Data */}
                          <td className={`py-12 px-20 border-start border-end ${isDiff ? "bg-white" : ""}`}>
                            {renderValue(row.master, isDiff)}
                          </td>

                          {/* Google Business Profile */}
                          <td className={`py-12 px-20 border-end ${isDiff ? "bg-white" : ""}`}>
                            {renderValue(row.gbp, isDiff)}
                          </td>

                          {/* Google Places Data */}
                          <td className={`py-12 px-20 border-end ${isDiff ? "bg-white" : ""}`}>
                            {renderValue(row.places, isDiff)}
                          </td>

                          {/* Diff Status Badge */}
                          <td className="py-12 px-16 text-center">
                            {isMatch ? (
                              <span className="badge bg-success-50 text-success-600 border border-success-200 px-8 py-4 radius-6 text-2xs d-inline-flex align-items-center gap-1">
                                <Icon icon="solar:check-circle-bold" /> Match
                              </span>
                            ) : isDiff ? (
                              <span className="badge bg-warning-100 text-warning-800 border border-warning-300 px-8 py-4 radius-6 text-2xs d-inline-flex align-items-center gap-1 fw-bold">
                                <Icon icon="solar:danger-triangle-bold" /> Differs
                              </span>
                            ) : (
                              <span className="badge bg-neutral-100 text-secondary-light px-8 py-4 radius-6 text-2xs">
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* MODAL FOOTER */}
          <div className="modal-footer bg-neutral-50 border-top px-24 py-12 d-flex align-items-center justify-content-between">
            <div className="text-xs text-secondary-light">
              <span>Showing snapshot data active as of Master version <strong>v{selectedVersion}</strong>.</span>
            </div>
            <button
              type="button"
              className="btn btn-secondary radius-8 px-20 py-8 text-sm"
              onClick={onClose}
            >
              Close Comparison
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LocationCompareModal;

